/**
 * Custom Views Hooks
 * React Query hooks for custom views
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import customViewsService, {
  type CustomView,
  type CreateCustomViewData,
  type UpdateCustomViewData,
  type ReorderView,
  type UpdateViewSharesData
} from '@/services/customViews.service';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

/**
 * Get all custom views
 */
export function useCustomViews() {
  return useQuery({
    queryKey: ['custom-views'],
    queryFn: () => customViewsService.getCustomViews(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get a specific custom view
 */
export function useCustomView(id: string) {
  return useQuery({
    queryKey: ['custom-views', id],
    queryFn: () => customViewsService.getCustomView(id),
    enabled: !!id,
  });
}

/**
 * Create a new custom view
 */
export function useCreateCustomView() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCustomViewData) => customViewsService.createCustomView(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-views'] });
      toast.success(t('errors:view.created', 'Ansicht erfolgreich erstellt'));
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('errors:view.createFailed', 'Fehler beim Erstellen der Ansicht'));
    },
  });
}

/**
 * Update a custom view
 */
export function useUpdateCustomView() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCustomViewData }) =>
      customViewsService.updateCustomView(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['custom-views'] });
      queryClient.invalidateQueries({ queryKey: ['custom-views', variables.id] });
      toast.success(t('errors:view.updated', 'Ansicht erfolgreich aktualisiert'));
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('errors:view.updateFailed', 'Fehler beim Aktualisieren der Ansicht'));
    },
  });
}

/**
 * Delete a custom view
 */
export function useDeleteCustomView() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => customViewsService.deleteCustomView(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-views'] });
      toast.success(t('errors:view.deleted', 'Ansicht erfolgreich gelöscht'));
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('errors:view.deleteFailed', 'Fehler beim Löschen der Ansicht'));
    },
  });
}

/**
 * Reorder custom views
 */
export function useReorderCustomViews() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (views: ReorderView[]) => customViewsService.reorderCustomViews(views),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-views'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('errors:view.reorderFailed', 'Fehler beim Neu-Sortieren der Ansichten'));
    },
  });
}

/**
 * Get shares for a custom view
 */
export function useViewShares(viewId: string | null) {
  return useQuery({
    queryKey: ['view-shares', viewId],
    queryFn: () => customViewsService.getViewShares(viewId!),
    enabled: !!viewId,
  });
}

/**
 * Update shares for a custom view
 */
export function useUpdateViewShares() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ viewId, data }: { viewId: string; data: UpdateViewSharesData }) =>
      customViewsService.updateViewShares(viewId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['view-shares', variables.viewId] });
      queryClient.invalidateQueries({ queryKey: ['custom-views'] });
      toast.success(t('errors:view.shareUpdated', 'Freigaben erfolgreich aktualisiert'));
    },
    onError: (error: any) => {
      const errorData = error.response?.data;
      // Check for organization access denied error
      if (errorData?.code === 'ORGANIZATION_ACCESS_DENIED') {
        toast.error(errorData.message, {
          description: errorData.details,
          duration: 8000,
        });
      } else {
        toast.error(errorData?.message || t('errors:view.shareUpdateFailed', 'Fehler beim Aktualisieren der Freigaben'));
      }
    },
  });
}

/**
 * Remove a specific share
 */
export function useRemoveShare() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ viewId, shareId }: { viewId: string; shareId: string }) =>
      customViewsService.removeShare(viewId, shareId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['view-shares', variables.viewId] });
      queryClient.invalidateQueries({ queryKey: ['custom-views'] });
      toast.success(t('errors:view.shareRemoved', 'Freigabe erfolgreich entfernt'));
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('errors:view.shareRemoveFailed', 'Fehler beim Entfernen der Freigabe'));
    },
  });
}
